/**
 * Shared clipboard helpers for admin copy actions.
 */

/**
 * @param {string[]} emails
 * @param {'comma'|'newline'} style
 * @returns {string}
 */
export function formatEmailList(emails, style = 'comma') {
  const list = [
    ...new Set(
      (emails || [])
        .map((e) => (e || '').toString().trim())
        .filter(Boolean)
    ),
  ];
  if (style === 'newline') return list.join('\n');
  return list.join(', ');
}

/**
 * Coerce a stored email field to a short plain address for clipboard (avoids copying huge HTML).
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeEmailForClipboard(raw) {
  const MAX = 320;
  if (raw == null) return '';
  const str = String(raw).trim();
  if (!str) return '';

  const mailto = str.match(/mailto:([^"'>\s?#]+)/i);
  if (mailto) {
    let addr = mailto[1];
    try {
      addr = decodeURIComponent(addr);
    } catch {
      /* ignore */
    }
    addr = addr.split('?')[0].trim();
    const fromMailto = addr.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (fromMailto) return fromMailto[0].slice(0, 254);
    if (addr.length <= MAX) return addr.replace(/[\r\n\t]/g, ' ');
  }

  const stripped = str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const m = stripped.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  if (m) return m[0].slice(0, 254);

  const plain = stripped.replace(/[\r\n\t]/g, ' ');
  return plain.slice(0, MAX);
}

/**
 * @param {string} text
 * @param {{ fallback?: 'prompt'|'none'; onSuccess?: () => void }} [options]
 * @returns {Promise<boolean>} true if writeText succeeded
 */
export async function copyText(text, options = {}) {
  const { fallback = 'prompt', onSuccess } = options;
  const str = text == null ? '' : String(text);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(str);
      onSuccess?.();
      return true;
    }
  } catch (err) {
    console.error('Copy failed:', err);
    // fall through
  }
  if (fallback === 'prompt' && str.length > 0) {
    window.prompt('Copy to clipboard (Ctrl+C, then Enter):', str);
  }
  return false;
}

/**
 * @param {{ id: string; header?: string; label?: string }[]} columns
 * @param {Record<string, string>[]} rows
 * @returns {string}
 */
export function buildTsvTable(columns, rows) {
  const headers = columns.map((c) => c.header || c.label || c.id);
  const headerLine = headers.join('\t');
  const lines = rows.map((row) =>
    columns
      .map((c) => String(row[c.id] ?? '').replace(/\r?\n/g, ' ').replace(/\t/g, ' '))
      .join('\t')
  );
  return [headerLine, ...lines].join('\n');
}

/**
 * Human-readable value for a native HTMLSelectElement. Prefer this over innerText or
 * textContent on the element: many environments concatenate every option label.
 * @param {HTMLSelectElement | null | undefined} el
 * @returns {string}
 */
export function getSelectDisplayText(el) {
  if (!el || el.tagName !== 'SELECT') return '';
  const opt = el.selectedOptions && el.selectedOptions[0];
  if (opt) return (opt.text ?? '').trim();
  const i = el.selectedIndex;
  if (i >= 0 && el.options && el.options[i]) return (el.options[i].text ?? '').trim();
  return String(el.value ?? '').trim();
}
